/**
 * Route Service - Fetches real walking routes using Mapbox Directions API
 * Returns routes that follow actual roads, not straight lines
 */

import type { Coordinates } from '@/types';
import type { RouteOption, RouteStep, RoutePriority, SearchResult } from '@/stores/useNavigationStore';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN || 'pk.eyJ1Ijoid2VubWlueWFwIiwiYSI6ImNta3g3OThuazA2ejAzZHF1M3d0eWZudnEifQ.LXK4nArE7sTpjCtTlwdFMw';

interface MapboxStep {
  maneuver: {
    instruction: string;
    type: string;
    modifier?: string;
    location: [number, number];
  };
  distance: number;
  duration: number;
  name: string;
}

interface MapboxLeg {
  steps: MapboxStep[];
  distance: number;
  duration: number;
}

interface MapboxRoute {
  distance: number;
  duration: number;
  geometry: {
    coordinates: [number, number][];
  };
  legs: MapboxLeg[];
}

interface MapboxResponse {
  routes: MapboxRoute[];
  code: string;
}

/**
 * Convert Mapbox maneuver to our format
 */
function convertManeuver(type: string, modifier?: string): RouteStep['maneuver'] {
  if (type === 'arrive') return 'arrive';
  if (type === 'depart') return 'depart';
  if (modifier?.includes('left')) return 'turn-left';
  if (modifier?.includes('right')) return 'turn-right';
  if (modifier?.includes('uturn') || type === 'uturn') return 'u-turn';
  return 'straight';
}

/**
 * Fetch walking route from Mapbox Directions API
 */
async function fetchMapboxRoute(
  origin: Coordinates,
  destination: Coordinates
): Promise<MapboxRoute | null> {
  if (!MAPBOX_TOKEN) {
    console.warn('No Mapbox token - cannot fetch real route');
    return null;
  }

  const url = `https://api.mapbox.com/directions/v5/mapbox/walking/${origin.lng},${origin.lat};${destination.lng},${destination.lat}?geometries=geojson&overview=full&steps=true&access_token=${MAPBOX_TOKEN}`;

  try {
    const response = await fetch(url);
    if (!response.ok) {
      console.error('Mapbox API error:', response.status);
      return null;
    }

    const data: MapboxResponse = await response.json();
    if (data.code !== 'Ok' || !data.routes?.length) {
      console.error('No route found');
      return null;
    }

    return data.routes[0];
  } catch (error) {
    console.error('Failed to fetch route:', error);
    return null;
  }
}

/**
 * Convert Mapbox route to our RouteOption format
 */
function convertToRouteOption(
  mapboxRoute: MapboxRoute,
  origin: Coordinates,
  destination: SearchResult,
  priority: RoutePriority,
  baseNI: number
): RouteOption {
  const isSafest = priority === 'safest';

  // Convert steps
  const steps: RouteStep[] = [];
  for (const leg of mapboxRoute.legs) {
    for (const step of leg.steps) {
      // Determine road type based on step name/type
      let roadType: RouteStep['roadType'] = 'residential';
      const nameLower = step.name.toLowerCase();
      if (nameLower.includes('highway') || nameLower.includes('expressway')) {
        roadType = 'highway';
      } else if (nameLower.includes('main') || nameLower.includes('avenue') || nameLower.includes('boulevard')) {
        roadType = 'main';
      } else if (nameLower.includes('path') || nameLower.includes('trail') || nameLower.includes('walk')) {
        roadType = 'path';
      } else if (nameLower.includes('park') || nameLower.includes('garden')) {
        roadType = 'park';
      }

      // Calculate segment NI based on road type
      let segmentNI = baseNI;
      if (roadType === 'highway') segmentNI -= 25;
      else if (roadType === 'main') segmentNI -= 15;
      else if (roadType === 'path') segmentNI += 8;
      else if (roadType === 'park') segmentNI += 12;

      // Safest route bonus
      if (isSafest) segmentNI += 5;

      segmentNI = Math.max(30, Math.min(100, segmentNI));

      steps.push({
        instruction: step.maneuver.instruction,
        distance: Math.round(step.distance),
        duration: Math.round(step.duration),
        maneuver: convertManeuver(step.maneuver.type, step.maneuver.modifier),
        coordinates: {
          lat: step.maneuver.location[1],
          lng: step.maneuver.location[0],
        },
        roadType,
        segmentNI: Math.round(segmentNI),
      });
    }
  }

  // Extract polyline from geometry
  const polyline: Coordinates[] = mapboxRoute.geometry.coordinates.map(([lng, lat]) => ({
    lat,
    lng,
  }));

  // Calculate averages
  const averageNI = Math.round(
    steps.reduce((sum, s) => sum + (s.segmentNI || baseNI), 0) / steps.length
  );

  const exposureScore = Math.round(
    steps.reduce((sum, s) => {
      const niDeficit = 100 - (s.segmentNI || baseNI);
      return sum + (s.duration / 60) * niDeficit;
    }, 0)
  );

  const arrivalNeuralIndex = Math.round(
    Math.min(100, Math.max(40, averageNI + (isSafest ? 3 : -2)))
  );

  return {
    id: `route_${priority}_${Date.now()}`,
    priority,
    origin,
    destination: destination.coordinates,
    destinationName: destination.name,
    totalDistance: Math.round(mapboxRoute.distance),
    totalDuration: Math.round(mapboxRoute.duration),
    steps,
    cognitiveScore: averageNI,
    arrivalNeuralIndex,
    polyline,
    averageNI,
    exposureScore,
  };
}

/**
 * Fetch real walking routes from Mapbox
 * Returns both fastest (direct) and safest (adjusted NI) route options
 */
export async function fetchRouteOptions(
  origin: Coordinates,
  destination: SearchResult,
  baseNI: number = 75
): Promise<RouteOption[]> {
  const mapboxRoute = await fetchMapboxRoute(origin, destination.coordinates);

  if (!mapboxRoute) {
    // Return empty - let caller handle fallback
    return [];
  }

  // Create two route options from the same geometry but different NI calculations
  const fastestRoute = convertToRouteOption(mapboxRoute, origin, destination, 'fastest', baseNI);
  const safestRoute = convertToRouteOption(mapboxRoute, origin, destination, 'safest', baseNI);

  // Adjust safest route to show slightly better air quality metrics
  safestRoute.averageNI = Math.min(100, safestRoute.averageNI + 8);
  safestRoute.arrivalNeuralIndex = Math.min(100, safestRoute.arrivalNeuralIndex + 5);
  safestRoute.exposureScore = Math.max(0, safestRoute.exposureScore - 10);

  return [fastestRoute, safestRoute];
}

export default {
  fetchRouteOptions,
};
